import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { encerrarSessao, lerSessao, renovarSessao, salvarSessao, sessaoExpirada, mensagemDeErro } from "./apiVotar";
import useAbaUnica from "./abaUnica";
import { obterRespondidas, pendentes as perguntasPendentes, salvarRespondida } from "./progressoVotos";
import GraficoResultado, { calcularResultado } from "./GraficoResultado";
import "./votar.css";

// "Bloco A · Unidade 101" (partes ausentes são omitidas)
export const rotuloUnidade = unidade => {
  if (!unidade) return "";
  const partes = [];
  if (unidade.bloco) partes.push(`Bloco ${unidade.bloco}`);
  if (unidade.numero) partes.push(`Unidade ${unidade.numero}`);
  return partes.join(" · ") || unidade.descricao || "Unidade votante";
};

// Cabeçalho fixo em todas as telas da votação: sempre mostra o condomínio e a unidade que está votando.
const Topo = ({ sessao, onSair, onVoltar, titulo }) => {
  const { unidade } = sessao;
  const responsavel = unidade.responsavel && unidade.responsavel.nome;
  return (
    <header className="vv-cabecalho">
      <div className="vv-topo">
        {onVoltar ? (
          <button type="button" className="vv-voltar" onClick={onVoltar} aria-label="Voltar">
            ←
          </button>
        ) : (
          <span className="vv-marca">VOTE ASSEMBLEIA</span>
        )}
        <span className="vv-topo-titulo">{titulo}</span>
        <button type="button" className="vv-sair" onClick={onSair}>
          Sair
        </button>
      </div>
      <div className="vv-identidade" aria-label="Unidade votante">
        <span className="vv-identidade-condominio">🏢 {unidade.condominio && unidade.condominio.nome}</span>
        <span className="vv-identidade-unidade">
          {rotuloUnidade(unidade)}
          {responsavel ? ` · ${responsavel}` : ""}
        </span>
      </div>
    </header>
  );
};

const TelaCodigo = ({ codigoInicial, aviso, onEntrar }) => {
  const [codigo, setCodigo] = useState(codigoInicial || "");
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);

  const enviar = async e => {
    e.preventDefault();
    if (!codigo.trim()) return setErro("Informe o código de acesso.");
    setCarregando(true);
    setErro("");
    try {
      await onEntrar(codigo);
    } catch (resposta) {
      setErro(resposta instanceof Error ? resposta.message : mensagemDeErro(resposta));
      setCarregando(false);
    }
  };

  return (
    <div className="vv-centro">
      <form className="vv-cartao vv-login" onSubmit={enviar}>
        <span className="vv-marca">VOTE ASSEMBLEIA</span>
        <h1>Votação online</h1>
        <p className="vv-suave">Digite o código de acesso que você recebeu do síndico ou da administradora.</p>
        <ol className="vv-passos">
          <li>Informe seu código</li>
          <li>Escolha a votação</li>
          <li>Vote em cada pauta</li>
        </ol>
        <input
          className="vv-campo"
          value={codigo}
          onChange={e => setCodigo(e.target.value)}
          placeholder="Código de acesso"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
          autoFocus
          aria-label="Código de acesso"
        />
        {aviso && <p className="vv-aviso" role="status">{aviso}</p>}
        {erro && <p className="vv-erro" role="alert">{erro}</p>}
        <button type="submit" className="vv-botao" disabled={carregando}>
          {carregando ? "Validando..." : "Entrar"}
        </button>
      </form>
    </div>
  );
};

const Lista = ({ sessao, abaInicial, aviso, onAbrir, onResultado }) => {
  const [aba, setAba] = useState(abaInicial || "abertas");
  const [itens, setItens] = useState(null);
  const [erro, setErro] = useState("");
  const condominioId = sessao.unidade.condominio._id;

  const carregar = useCallback(async () => {
    setItens(null);
    setErro("");
    try {
      const busca = aba === "abertas" ? api.abertas : api.encerradas;
      setItens(await busca(sessao.token, condominioId));
    } catch (resposta) {
      setErro(mensagemDeErro(resposta));
      setItens([]);
    }
  }, [aba, sessao.token, condominioId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // nada mais para votar: leva o votante direto para a aba de resultados
  useEffect(() => {
    if (abaInicial) return undefined;
    let ativo = true;
    (async () => {
      try {
        const abertas = await api.abertas(sessao.token, condominioId);
        let falta = false;
        for (const v of abertas) {
          const detalhe = await api.votacao(sessao.token, v._id);
          const feitas = await obterRespondidas(sessao, v._id);
          if (perguntasPendentes(detalhe.perguntas, feitas).length > 0) {
            falta = true;
            break;
          }
        }
        if (ativo && !falta) setAba("encerradas");
      } catch (e) {
        /* sem a checagem, fica na aba padrão */
      }
    })();
    return () => {
      ativo = false;
    };
    // dependências estáveis: a sessão é relida (objeto novo) sem mudar de votante
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessao.token, sessao.unidade._id, condominioId, abaInicial]);

  return (
    <div className="vv-pagina">
      <div className="vv-abas" role="tablist">
        {[["abertas", "Para votar"], ["encerradas", "Resultados"]].map(([chave, rotulo]) => (
          <button
            key={chave}
            type="button"
            role="tab"
            aria-selected={aba === chave}
            className={aba === chave ? "vv-aba ativa" : "vv-aba"}
            onClick={() => setAba(chave)}
          >
            {rotulo}
          </button>
        ))}
        <button type="button" className="vv-atualizar" onClick={carregar}>
          Atualizar
        </button>
      </div>
      {aviso && aba === "encerradas" && <p className="vv-ok" role="status">{aviso}</p>}
      {erro && <p className="vv-erro">{erro}</p>}
      {itens === null && <p className="vv-suave">Carregando...</p>}
      {itens && itens.length === 0 && !erro && (
        <p className="vv-vazio">{aba === "abertas" ? "Não existem votações em aberto." : "Nenhuma votação encerrada ainda."}</p>
      )}
      {itens &&
        itens.map(v => (
          <button
            key={v._id}
            type="button"
            className="vv-cartao vv-item vv-col"
            onClick={() => (aba === "abertas" ? onAbrir(v) : onResultado(v))}
          >
            <span className={aba === "abertas" ? "vv-chip aberta" : "vv-chip encerrada"}>
              {aba === "abertas" ? "Aberta" : "Encerrada"}
            </span>
            <strong>{v.titulo}</strong>
            <span className="vv-suave">
              {aba === "abertas" ? "Vote até" : "Encerrada em"} {v.dataFinalReuniaoFmt}
            </span>
            <span className="vv-acao-texto">{aba === "abertas" ? "Votar agora" : "Ver resultado e gráficos"}</span>
            <span className="vv-seta">›</span>
          </button>
        ))}
    </div>
  );
};

const Links = ({ pergunta }) => {
  const docs = pergunta.documentos || [];
  const links = pergunta.links || [];
  if (!docs.length && !links.length) return null;
  return (
    <div className="vv-anexos">
      <strong>Documentos e links</strong>
      {docs.map(d => (
        <a key={d._id} href={d.url} target="_blank" rel="noopener noreferrer">
          📄 {d.nome}
        </a>
      ))}
      {links.map(l => (
        <a key={l._id} href={l.url} target="_blank" rel="noopener noreferrer">
          🔗 {l.nome}
        </a>
      ))}
    </div>
  );
};

const Perguntas = ({ sessao, votacaoResumo, onVoltar, onConcluir }) => {
  const [votacao, setVotacao] = useState(null);
  const [respondidas, setRespondidas] = useState(null);
  const enviandoRef = useRef(false);
  const montadoRef = useRef(true);
  useEffect(() => () => { montadoRef.current = false; }, []);
  const [atual, setAtual] = useState(null);
  const [resposta, setResposta] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [concluida, setConcluida] = useState(null);

  useEffect(() => {
    let ativo = true;
    Promise.all([api.votacao(sessao.token, votacaoResumo._id), obterRespondidas(sessao, votacaoResumo._id)])
      .then(([dados, feitas]) => {
        if (!ativo) return;
        setRespondidas(feitas);
        setVotacao(dados);
        // nada para votar: segue direto para os resultados
        if (perguntasPendentes(dados.perguntas, feitas).length === 0 && (dados.perguntas || []).length > 0) {
          onConcluir("Você já votou em todas as pautas desta votação.");
        }
      })
      .catch(r => ativo && setErro(mensagemDeErro(r)));
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessao.token, votacaoResumo._id]);

  const abrirPergunta = async pergunta => {
    if (respondidas.includes(String(pergunta._id))) return; // já votou: não reabre
    setErro("");
    setConcluida(null);
    try {
      const validacao = await api.validarHora(sessao.token, votacaoResumo._id);
      if (!validacao.votacaoValida) return setErro(validacao.mensagem);
      setResposta(null);
      setAtual(pergunta);
    } catch (r) {
      setErro(mensagemDeErro(r));
    }
  };

  const confirmar = async () => {
    // trava síncrona: dois cliques seguidos antes do React atualizar o botão não enviam dois votos
    if (enviandoRef.current || !atual || resposta === null) return;
    if (respondidas.includes(String(atual._id))) return;
    enviandoRef.current = true;
    setEnviando(true);
    setErro("");
    try {
      await api.confirmarVoto(sessao.token, {
        perguntaId: atual._id,
        respostaId: resposta,
        unidadeId: sessao.unidade._id
      });
      salvarRespondida(votacaoResumo._id, sessao.unidade._id, String(atual._id));
      const novas = [...respondidas, String(atual._id)];
      setRespondidas(novas);
      setConcluida(atual._id);
      setAtual(null);
      if (perguntasPendentes(votacao.perguntas, novas).length === 0) {
        onConcluir("✔ Voto registrado! Você votou em todas as pautas. Os resultados aparecem aqui quando a votação encerrar.");
      }
    } catch (r) {
      setErro(mensagemDeErro(r));
    } finally {
      enviandoRef.current = false;
      if (montadoRef.current) setEnviando(false);
    }
  };

  if (!votacao || !respondidas) {
    return (
      <div className="vv-pagina">
        {erro ? <p className="vv-erro">{erro}</p> : <p className="vv-suave">Carregando...</p>}
        {erro && <button type="button" className="vv-botao secundario" onClick={onVoltar}>Voltar</button>}
      </div>
    );
  }

  const perguntas = votacao.perguntas || [];
  const feitas = perguntas.filter(p => respondidas.includes(String(p._id))).length;
  const proxima = perguntas.find(p => !respondidas.includes(String(p._id)));
  const progresso = perguntas.length ? (feitas / perguntas.length) * 100 : 0;

  if (atual) {
    const indice = perguntas.findIndex(p => p._id === atual._id) + 1;
    const escolhida = atual.respostas.find(r => r._id === resposta);
    return (
      <div className="vv-pagina">
        <div className="vv-passo">Pauta {indice} de {perguntas.length}</div>
        <div className="vv-cartao">
          <h2 className="vv-pergunta-titulo">{atual.titulo}</h2>
          <p className="vv-suave">Escolha uma opção:</p>
          <div className="vv-opcoes" role="radiogroup">
            {atual.respostas.map(r => (
              <label key={r._id} className={resposta === r._id ? "vv-opcao marcada" : "vv-opcao"}>
                <input type="radio" name="resposta" value={r._id} checked={resposta === r._id} onChange={() => setResposta(r._id)} />
                <span>{r.titulo}</span>
              </label>
            ))}
          </div>
          <Links pergunta={atual} />
        </div>
        {erro && <p className="vv-erro" role="alert">{erro}</p>}
        <div className="vv-barra-fixa">
          <button type="button" className="vv-botao secundario" onClick={() => { setAtual(null); setErro(""); }} disabled={enviando}>
            Voltar
          </button>
          <button type="button" className="vv-botao" onClick={confirmar} disabled={resposta === null || enviando}>
            {enviando ? "Enviando..." : escolhida ? `Votar em "${escolhida.titulo}"` : "Escolha uma opção"}
          </button>
        </div>
      </div>
    );
  }

  const concluidaTodas = perguntas.length > 0 && feitas === perguntas.length;

  return (
    <div className="vv-pagina">
      <div className="vv-cartao">
        <span className="vv-chip aberta">Votação aberta</span>
        <h2>{votacao.titulo}</h2>
        {votacao.local && <p className="vv-suave">📍 {votacao.local}</p>}
        <p className="vv-suave">Encerra em {votacaoResumo.dataFinalReuniaoFmt}</p>
        {votacao.urlTransmissao && (
          <a className="vv-destaque" href={votacao.urlTransmissao} target="_blank" rel="noopener noreferrer">
            ▶ Acompanhar transmissão
          </a>
        )}
        {[votacao.documentoConvocacao, votacao.documentoAta].filter(Boolean).map(d => (
          <a key={d._id} href={d.url} target="_blank" rel="noopener noreferrer" className="vv-link">
            📄 {d.nome || "Documento"}
          </a>
        ))}
        <div className="vv-progresso" aria-label={`${feitas} de ${perguntas.length} pautas votadas`}>
          <div className="vv-progresso-valor" style={{ width: `${progresso}%` }} />
        </div>
        <span className="vv-suave">{feitas} de {perguntas.length} pautas votadas</span>
      </div>

      {concluida && <p className="vv-ok" role="status">✔ Voto registrado com sucesso!</p>}
      {erro && <p className="vv-erro" role="alert">{erro}</p>}

      {concluidaTodas ? (
        <div className="vv-cartao vv-fim">
          <div className="vv-fim-icone">🎉</div>
          <strong>Você votou em todas as pautas!</strong>
          <p className="vv-suave">Quando a votação for encerrada, o resultado com gráficos fica na aba "Resultados".</p>
          <button type="button" className="vv-botao" onClick={() => onConcluir("")}>Ver resultados</button>
        </div>
      ) : (
        proxima && (
          <button type="button" className="vv-botao vv-grande" onClick={() => abrirPergunta(proxima)}>
            {feitas === 0 ? "Começar a votar" : "Votar na próxima pauta"}
          </button>
        )
      )}

      <h3 className="vv-subtitulo">Pautas</h3>
      {perguntas.map((p, i) => {
        const feita = respondidas.includes(String(p._id));
        return (
          <button
            key={p._id}
            type="button"
            className={feita ? "vv-cartao vv-item feita" : "vv-cartao vv-item"}
            onClick={() => !feita && abrirPergunta(p)}
            disabled={feita}
          >
            <span className="vv-numero">{feita ? "✔" : i + 1}</span>
            <span className="vv-item-texto">
              <strong>{p.titulo}</strong>
              <span className="vv-suave">{feita ? "Voto registrado" : "Aguardando seu voto"}</span>
            </span>
            {!feita && <span className="vv-seta">›</span>}
          </button>
        );
      })}
    </div>
  );
};

const Resultado = ({ sessao, votacaoResumo }) => {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => {
    api
      .resultado(sessao.token, sessao.unidade.condominio._id, votacaoResumo._id)
      .then(setDados)
      .catch(r => setErro(mensagemDeErro(r)));
  }, [sessao, votacaoResumo._id]);

  if (erro) return <div className="vv-pagina"><p className="vv-erro">{erro}</p></div>;
  if (!dados) return <div className="vv-pagina"><p className="vv-suave">Carregando resultado...</p></div>;

  const resumo = dados.perguntas.map(calcularResultado);
  const maiorParticipacao = Math.max(0, ...resumo.map(r => r.total));
  const documentos = [dados.votacao && dados.votacao.documentoAta, dados.votacao && dados.votacao.documentoConvocacao].filter(Boolean);

  return (
    <div className="vv-pagina">
      <div className="vv-cartao vv-destaque-topo">
        <span className="vv-chip encerrada">Votação encerrada</span>
        <h2>{votacaoResumo.titulo}</h2>
        <div className="vv-numeros">
          <div><b>{dados.perguntas.length}</b><span>pautas</span></div>
          <div><b>{maiorParticipacao}</b><span>votos na pauta mais votada</span></div>
          <div><b>{votacaoResumo.dataFinalReuniaoFmt}</b><span>encerramento</span></div>
        </div>
        {documentos.map(d => (
          <a key={d._id} href={d.url} target="_blank" rel="noopener noreferrer" className="vv-link">
            📄 {d.nome || "Documento"}
          </a>
        ))}
      </div>
      {dados.perguntas.map((p, i) => (
        <GraficoResultado key={p._id} pergunta={p} numero={i + 1} />
      ))}
    </div>
  );
};

const VERIFICACAO_MS = 15 * 1000;

const Bloqueada = ({ onAssumir }) => (
  <div className="vv-raiz">
    <div className="vv-centro">
      <div className="vv-cartao vv-login">
        <span className="vv-marca">VOTE ASSEMBLEIA</span>
        <h1>Votação aberta em outra aba</h1>
        <p className="vv-suave">
          Para evitar votos duplicados, só uma aba por vez pode votar. Feche a outra aba ou use esta.
        </p>
        <button type="button" className="vv-botao" onClick={onAssumir}>Usar esta aba</button>
      </div>
    </div>
  </div>
);

const Votar = ({ match, history }) => {
  const { ativa, assumir } = useAbaUnica();
  const [sessao, setSessao] = useState(() => {
    const gravada = lerSessao();
    return gravada && !sessaoExpirada(gravada) ? gravada : null;
  });
  const [aviso, setAviso] = useState("");
  const [tela, setTela] = useState({ nome: "lista" });
  const codigoUrl = match && match.params && match.params.codigo;
  const sessaoRef = useRef(sessao);
  sessaoRef.current = sessao;

  // encerra no servidor e apaga tudo que ficou no navegador (sessão, votos respondidos, identificador do aparelho)
  const encerrar = useCallback(async motivo => {
    const atual = sessaoRef.current || lerSessao();
    sessaoRef.current = null;
    setSessao(null);
    setTela({ nome: "lista" });
    setAviso(motivo || "");
    await encerrarSessao(atual);
  }, []);

  const entrar = async codigo => {
    const dados = await api.entrar(codigo);
    const unidade = (dados.unidades || []).find(u => u.hash === codigo.trim().toLowerCase()) || (dados.unidades || [])[0];
    if (!unidade) throw new Error("Unidade votante não encontrada.");
    const nova = salvarSessao({ token: dados.token, unidade });
    setAviso("");
    setSessao(nova);
    setTela({ nome: "lista" });
    if (codigoUrl) history.replace("/votar");
  };

  // sessão gravada e vencida (usuário voltou ao navegador depois de muito tempo): libera a unidade e limpa os dados
  useEffect(() => {
    if (!ativa) return;
    const gravada = lerSessao();
    if (gravada && sessaoExpirada(gravada)) {
      encerrarSessao(gravada).then(() => setAviso("Sua sessão expirou. Informe o código novamente."));
    }
  }, [ativa]);

  // aba que volta a ser a ativa relê a sessão (a outra aba pode ter saído ou entrado com outro código)
  useEffect(() => {
    if (!ativa) return;
    const gravada = lerSessao();
    setSessao(gravada && !sessaoExpirada(gravada) ? gravada : null);
  }, [ativa]);

  // expiração por inatividade/tempo total + renovação a cada ação do votante
  useEffect(() => {
    if (!sessao || !ativa) return undefined;
    let ultimaRenovacao = 0;
    const atividade = () => {
      const agora = Date.now();
      if (agora - ultimaRenovacao > VERIFICACAO_MS) {
        ultimaRenovacao = agora;
        renovarSessao();
      }
    };
    const eventos = ["click", "keydown", "touchstart", "scroll"];
    eventos.forEach(e => window.addEventListener(e, atividade, true));
    const intervalo = setInterval(() => {
      const gravada = lerSessao();
      if (!gravada) setSessao(null);
      else if (sessaoExpirada(gravada)) encerrar("Sua sessão expirou por inatividade. Informe o código novamente.");
    }, VERIFICACAO_MS);
    return () => {
      eventos.forEach(e => window.removeEventListener(e, atividade, true));
      clearInterval(intervalo);
    };
  }, [sessao, ativa, encerrar]);

  // token recusado pela API: encerra e volta para a tela do código
  useEffect(() => {
    if (!sessao || !ativa) return;
    api.abertas(sessao.token, sessao.unidade.condominio._id).catch(r => {
      if (r && r.status === 401) encerrar("Sua sessão expirou. Informe o código novamente.");
    });
  }, [sessao, ativa, encerrar]);

  if (!ativa) return <Bloqueada onAssumir={assumir} />;

  if (!sessao) {
    return (
      <div className="vv-raiz">
        <TelaCodigo codigoInicial={codigoUrl} aviso={aviso} onEntrar={entrar} />
      </div>
    );
  }

  const voltar = () => setTela({ nome: "lista" });
  const titulo = { lista: "Votações", perguntas: "Votação", resultado: "Resultado" }[tela.nome];

  return (
    <div className="vv-raiz">
      <Topo sessao={sessao} onSair={() => encerrar("Você saiu. Seus dados foram removidos deste navegador.")} onVoltar={tela.nome === "lista" ? null : voltar} titulo={titulo} />
      {tela.nome === "lista" && (
        <Lista
          key={`${tela.aba || "auto"}-${tela.aviso || ""}`}
          sessao={sessao}
          abaInicial={tela.aba}
          aviso={tela.aviso}
          onAbrir={v => setTela({ nome: "perguntas", votacao: v })}
          onResultado={v => setTela({ nome: "resultado", votacao: v })}
        />
      )}
      {tela.nome === "perguntas" && <Perguntas sessao={sessao} votacaoResumo={tela.votacao} onVoltar={voltar} onConcluir={aviso => setTela({ nome: "lista", aba: "encerradas", aviso })} />}
      {tela.nome === "resultado" && <Resultado sessao={sessao} votacaoResumo={tela.votacao} />}
    </div>
  );
};

export default Votar;
